import React from 'react';
import { Lightbulb } from 'lucide-react';
import KPICard from '../../components/shared/KPICard';
import SectionDivider from '../../components/shared/SectionDivider';
import StatusPill from '../../components/shared/StatusPill';
import AreaChart from '../../components/charts/AreaChart';
import { consumerPlant, acVoltageTrend } from '../../data/mockData';

interface ParamCardProps {
  label: string;
  value: string;
  highlight?: boolean;
}

function ParamCard({ label, value, highlight = false }: ParamCardProps) {
  return (
    <div className="bg-bg-secondary rounded-input p-3 border border-border">
      <p className="text-xs text-text-tertiary">{label}</p>
      <p className={`text-sm font-bold mt-0.5 ${highlight ? 'text-warning' : 'text-text-primary'}`}>{value}</p>
    </div>
  );
}

export default function ConsumerElectrical() {
  const { electrical } = consumerPlant;

  return (
    <div className="p-6 space-y-6">
      {/* KPIs */}
      <div className="flex gap-4">
        <div className="bg-consumer-amber rounded-card p-4 flex-1 border border-orange-200 shadow-card">
          <p className="text-xs text-orange-700 font-medium uppercase tracking-wide">Inverter Status</p>
          <div className="mt-1">
            <StatusPill status="online" label="ON" />
          </div>
          <p className="text-xs text-text-secondary mt-1">Running normally</p>
        </div>
        <div className="bg-consumer-amber rounded-card p-4 flex-1 border border-orange-200 shadow-card">
          <p className="text-xs text-orange-700 font-medium uppercase tracking-wide">Active Power</p>
          <p className="text-xl font-bold text-text-primary mt-1">{electrical.activePower} kW</p>
          <p className="text-xs text-text-secondary mt-1">AC output</p>
        </div>
        <div className="bg-consumer-amber rounded-card p-4 flex-1 border border-orange-200 shadow-card">
          <p className="text-xs text-orange-700 font-medium uppercase tracking-wide">Today On-time</p>
          <p className="text-xl font-bold text-text-primary mt-1">{electrical.onTimeHours} hrs</p>
          <p className="text-xs text-text-secondary mt-1">Since sunrise</p>
        </div>
        <div className="bg-consumer-amber rounded-card p-4 flex-1 border border-orange-200 shadow-card">
          <p className="text-xs text-orange-700 font-medium uppercase tracking-wide">Inverter Temp</p>
          <p className="text-xl font-bold text-warning mt-1">{electrical.inverterTemp}°C</p>
          <p className="text-xs text-warning mt-1">Elevated — monitor</p>
        </div>
      </div>

      {/* DC + AC side cards */}
      <div className="flex gap-4">
        {/* DC Side */}
        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="DC Side — MPPT Inputs" subtitle="String-level parameters" />
          <div className="grid grid-cols-2 gap-3">
            <ParamCard label="DC Voltage MPPT1" value={`${electrical.dcVoltage1} V`} highlight />
            <ParamCard label="DC Current MPPT1" value={`${electrical.dcCurrent1} A`} highlight />
            <ParamCard label="DC Power MPPT1" value={`${electrical.dcPower1} kW`} highlight />
            <ParamCard label="DC Voltage MPPT2" value={`${electrical.dcVoltage2} V`} highlight />
            <ParamCard label="DC Current MPPT2" value={`${electrical.dcCurrent2} A`} highlight />
            <ParamCard label="DC Power MPPT2" value={`${electrical.dcPower2} kW`} highlight />
          </div>
          <div className="mt-3 bg-orange-50 border border-orange-200 rounded-input p-3 flex gap-2">
            <Lightbulb size={13} className="text-warning flex-shrink-0 mt-0.5" />
            <p className="text-xs text-warning">
              MPPT 2 output is 8% lower than MPPT 1 — possible partial shading or soiling on string 2.
            </p>
          </div>
        </div>

        {/* AC Side */}
        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="AC Side — Grid Parameters" subtitle="Inverter output to grid" />
          <div className="grid grid-cols-2 gap-3">
            <ParamCard label="AC Voltage" value={`${electrical.acVoltage} V`} />
            <ParamCard label="AC Current" value={`${electrical.acCurrent} A`} />
            <ParamCard label="Frequency" value={`${electrical.frequency} Hz`} />
            <ParamCard label="Power Factor" value={`${electrical.powerFactor}`} />
            <ParamCard label="Reactive Power" value={`${electrical.reactivePower} kVAr`} />
            <ParamCard label="Apparent Power" value={`${electrical.apparentPower} kVA`} />
          </div>
        </div>
      </div>

      {/* AC Voltage trend */}
      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <SectionDivider title="AC Voltage Trend — Today" subtitle="Volts over time" />
        <AreaChart
          data={acVoltageTrend}
          xKey="time"
          yKey="voltage"
          color="#2472C8"
          unit=" V"
          yLabel="V"
          yDomain={[235, 248]}
          height={200}
        />
      </div>
    </div>
  );
}
